import React, { useMemo, useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Star,
  TrendingDown,
  Truck,
  Package,
  ExternalLink,
  BadgeCheck,
  Flame,
  ShieldCheck,
  Camera,
  Gauge,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import { trackClick } from "../lib/api";

const FILTERS = [
  { id: "all", label: "Tous", testid: "filter-pieces-all" },
  { id: "neuf", label: "Neuf", testid: "filter-pieces-neuf" },
  { id: "occasion", label: "Occasion", testid: "filter-pieces-occasion" },
];

export const SupplierComparator = ({
  offers,
  partLabel,
  categoryLabel,
  vehicle,
  loading,
}) => {
  const [filter, setFilter] = useState("all");

  const counts = useMemo(() => {
    const list = offers || [];
    const neuf = list.filter((o) => o.type_pieces !== "occasion").length;
    const occasion = list.filter((o) => o.type_pieces === "occasion").length;
    return { all: list.length, neuf, occasion };
  }, [offers]);

  const filtered = useMemo(() => {
    const list = offers || [];
    if (filter === "all") return list;
    if (filter === "neuf")
      return list.filter((o) => o.type_pieces !== "occasion");
    return list.filter((o) => o.type_pieces === "occasion");
  }, [filter, offers]);

  if (loading) {
    return (
      <div
        data-testid="supplier-comparator-loading"
        className="surface-card rounded-2xl p-8"
      >
        <div className="mb-4 h-6 w-48 rounded shimmer" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-48 rounded-xl shimmer" />
          ))}
        </div>
      </div>
    );
  }

  if (!offers || offers.length === 0) {
    // Cas spécial poids lourd : pas encore de fournisseurs configurés
    if (vehicle?.type_vehicule === "poids_lourd") {
      return (
        <div
          data-testid="supplier-comparator-pl-empty"
          className="surface-card animate-slide-up rounded-2xl border border-indigo-500/30 bg-indigo-500/5 p-8 text-center"
        >
          <div className="mx-auto mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-500/20 ring-1 ring-indigo-400/40">
            <Truck className="h-7 w-7 text-indigo-300" strokeWidth={1.8} />
          </div>
          <h3 className="font-heading text-xl font-bold text-slate-50">
            Fournisseurs poids lourds en cours d'intégration
          </h3>
          <p className="mx-auto mt-2 max-w-xl text-sm text-slate-400">
            On branche actuellement nos partenaires spécialisés PL
            (Mecaprem, Krone, Europart, etc.). Reviens très vite — ou laisse-nous ton
            e-mail pour être prévenu dès l'ouverture du comparateur poids lourds.
          </p>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-widest text-indigo-300">
            Véhicule reconnu : {vehicle.marque} {vehicle.modele}
          </p>
        </div>
      );
    }
    return null;
  }

  const handleClick = (offer) => {
    trackClick({
      supplier_id: offer.supplier_id,
      supplier_name: offer.supplier_name,
      plaque: vehicle?.plaque,
      part_query: partLabel,
      category_id: categoryLabel ? undefined : undefined,
      marque: vehicle?.marque,
      modele: vehicle?.modele,
    });
    window.open(offer.affiliate_url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      data-testid="supplier-comparator-grid"
      className="surface-card animate-slide-up rounded-2xl p-6 sm:p-8"
    >
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-400">
            Étape 3 · Comparateur multi-fournisseurs
          </span>
          <h3 className="mt-1 font-heading text-2xl font-bold text-slate-50 sm:text-3xl">
            {partLabel}
          </h3>
          <p className="mt-1 text-sm text-slate-400">
            {categoryLabel && (
              <span className="mr-2 inline-block">
                Catégorie <span className="text-slate-200">{categoryLabel}</span>
              </span>
            )}
            {filtered.length} offres comparées pour {vehicle?.marque} {vehicle?.modele}
          </p>
        </div>
        <Badge
          variant="outline"
          className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
        >
          <TrendingDown className="mr-1 h-3 w-3" /> Meilleur prix mis en avant
        </Badge>
      </div>

      {/* Filtre Neuf / Occasion / Tous */}
      <div
        data-testid="pieces-type-filter"
        className="mb-5 inline-flex flex-wrap items-center gap-1 rounded-full border border-slate-800 bg-slate-900/70 p-1"
      >
        {FILTERS.map((f) => {
          const active = filter === f.id;
          const count = counts[f.id] ?? 0;
          return (
            <button
              key={f.id}
              type="button"
              data-testid={f.testid}
              onClick={() => setFilter(f.id)}
              disabled={count === 0}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed ${
                active
                  ? "bg-cyan-500 text-slate-950 shadow-[0_4px_14px_rgba(6,182,212,0.3)]"
                  : "text-slate-300 hover:bg-slate-800 hover:text-cyan-300"
              }`}
            >
              {f.label}
              <span
                className={`font-mono text-[10px] rounded-full px-1.5 py-0.5 ${
                  active ? "bg-slate-950/20 text-slate-900" : "bg-slate-800 text-slate-400"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((offer, idx) => (
          <article
            key={offer.supplier_id}
            data-testid={`supplier-card-${offer.supplier_id}`}
            className={`surface-card-hover relative flex flex-col overflow-hidden rounded-2xl border p-5 ${
              offer.meilleur_prix
                ? "border-emerald-500/50 bg-emerald-500/5"
                : offer.type_pieces === "occasion"
                  ? "border-orange-500/40 bg-orange-500/5"
                  : "border-slate-800 bg-slate-900/60"
            }`}
            style={{ animationDelay: `${idx * 50}ms` }}
          >
            {offer.meilleur_prix && (
              <div className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-emerald-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-950">
                <Flame className="h-3 w-3" />
                Meilleur prix
              </div>
            )}
            {offer.type_pieces === "occasion" && !offer.meilleur_prix && (
              <div
                data-testid={`occasion-badge-${offer.supplier_id}`}
                className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-orange-500 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-orange-950"
              >
                Occasion
              </div>
            )}

            <div className="flex items-center gap-3">
              <div
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-heading text-lg font-black text-white shadow-md"
                style={{ background: offer.supplier_couleur }}
              >
                {offer.supplier_logo_initial}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate font-heading text-lg font-bold text-slate-50">
                    {offer.supplier_name}
                  </span>
                  {offer.type_pieces === "occasion" && offer.meilleur_prix && (
                    <span className="shrink-0 rounded bg-orange-500/20 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-orange-300">
                      Occasion
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                  <span className="font-semibold text-slate-200">
                    {offer.supplier_note}
                  </span>
                  <span>({offer.supplier_nb_avis.toLocaleString("fr-FR")} avis)</span>
                </div>
              </div>
            </div>

            <p className="mt-3 line-clamp-2 text-xs text-slate-500">
              {offer.supplier_slogan}
            </p>

            <div className="mt-4 flex items-end justify-between border-t border-slate-800/70 pt-4">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  Prix pièce
                </div>
                <div className="font-heading text-3xl font-extrabold text-slate-50">
                  {offer.prix.toFixed(2)}
                  <span className="text-lg text-slate-400"> €</span>
                </div>
                <div className="mt-1 text-[11px] text-slate-500">
                  + {offer.frais_port === 0 ? "Livraison offerte" : `${offer.frais_port.toFixed(2)} € port`}
                </div>
              </div>
              <Badge
                variant="outline"
                className="border-slate-700 bg-slate-900/70 text-[10px] uppercase tracking-wider text-slate-300"
              >
                {offer.supplier_badge}
              </Badge>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
              <div className="flex items-center gap-1.5 rounded-md bg-slate-900/80 px-2 py-1.5">
                <Truck className="h-3.5 w-3.5 text-cyan-400" />
                <span className="text-slate-300">{offer.delai_livraison}</span>
              </div>
              <div className="flex items-center gap-1.5 rounded-md bg-slate-900/80 px-2 py-1.5">
                <Package className="h-3.5 w-3.5 text-cyan-400" />
                <span className="text-slate-300">{offer.stock}</span>
              </div>
            </div>

            <div className="mt-3 flex items-center gap-2 rounded-md bg-slate-950/50 px-2 py-1.5 font-mono text-[10px] text-slate-500">
              <BadgeCheck className="h-3 w-3 text-cyan-400" />
              Réf. OEM {offer.reference_oem}
            </div>

            <Button
              data-testid={`supplier-affiliate-button-${offer.supplier_id}`}
              onClick={() => handleClick(offer)}
              className="mt-4 w-full gap-2 bg-cyan-500 font-semibold text-slate-950 hover:bg-cyan-400"
            >
              Voir chez {offer.supplier_name}
              <ExternalLink className="h-4 w-4" />
            </Button>
          </article>
        ))}
      </div>

      <p className="mt-6 text-center text-xs text-slate-500">
        Autopièce Malin est un comparateur indépendant. Les liens vers les fournisseurs sont des
        liens d'affiliation : le prix ne change pas pour vous, mais ils nous soutiennent.
      </p>
    </div>
  );
};
